import { delay } from '.';

import {
  sendAnalyticsError,
  sendAnalyticsInfo,
} from 'features/analytics/helpers';

export interface RetryOptions {
  baseDelay?: number;
  maxDelay?: number;
  maxAttempts?: number;
  enableJitter?: boolean;
  jitterFactor?: number;
}

export interface RetryContext {
  attemptNumber: number;
  maxAttempts: number;
  backoffDelay: number;
  isRetry: boolean;
}

export interface RetryAnalytics {
  method?: string;
  message?: string;
  roomUri?: string;
  isAcceptingAgent?: boolean;
  dispatch?: <T>(action: T) => T;
  [key: string]: any;
}

export type RetryOperation<T> = (context: RetryContext) => Promise<T>;
export type RetrySuccessHandler<T> = (result: T, context: RetryContext) => void;
export type RetryFailureHandler = (context: RetryContext) => void;
export type RetryAttemptHandler = (context: RetryContext) => void;

export class RetryManager {
  private readonly baseDelay: number;
  private readonly maxDelay: number;
  private readonly maxAttempts: number;
  private readonly enableJitter: boolean;
  private readonly jitterFactor: number;

  constructor(options: RetryOptions = {}) {
    this.baseDelay = options.baseDelay ?? 1000;
    this.maxDelay = options.maxDelay ?? 10000;
    this.maxAttempts = options.maxAttempts ?? 3;
    this.enableJitter = options.enableJitter ?? false;
    this.jitterFactor = options.jitterFactor ?? 0.25;
  }

  /**
   * Calculate exponential backoff delay with optional jitter
   */
  private calculateDelay(attemptNumber: number): number {
    const exponentialDelay = Math.min(
      this.baseDelay * Math.pow(2, attemptNumber - 1),
      this.maxDelay
    );

    if (!this.enableJitter) {
      return exponentialDelay;
    }

    const jitter = Math.random() * (exponentialDelay * this.jitterFactor);
    return Math.floor(exponentialDelay + jitter);
  }

  /**
   * Check if retry should continue
   */
  private shouldRetry(attemptNumber: number): boolean {
    return attemptNumber <= this.maxAttempts;
  }

  /**
   * Send analytics for retry attempts
   */
  private static sendAttemptAnalytics(
    context: RetryContext,
    analytics?: RetryAnalytics
  ): void {
    if (!analytics?.dispatch) {
      return;
    }

    const { dispatch, ...analyticsData } = analytics;

    if (context.isRetry) {
      dispatch(
        sendAnalyticsInfo({
          Method: analyticsData.method || 'RetryManager',
          message:
            analyticsData.message ||
            `Retrying operation (attempt ${context.attemptNumber}/${context.maxAttempts})`,
          attemptNumber: context.attemptNumber,
          maxAttempts: context.maxAttempts,
          backoffDelay: context.backoffDelay,
          ...analyticsData,
        })
      );
    } else {
      dispatch(
        sendAnalyticsInfo({
          Method: analyticsData.method || 'RetryManager',
          message:
            analyticsData.message ||
            `Starting operation attempt ${context.attemptNumber}`,
          attemptNumber: context.attemptNumber,
          ...analyticsData,
        })
      );
    }
  }

  /**
   * Send analytics for successful operations
   */
  private static sendSuccessAnalytics<T>(
    result: T,
    context: RetryContext,
    analytics?: RetryAnalytics
  ): void {
    if (!analytics?.dispatch) {
      return;
    }

    const { dispatch, ...analyticsData } = analytics;

    dispatch(
      sendAnalyticsInfo({
        Method: analyticsData.method || 'RetryManager',
        message:
          analyticsData.message ||
          `Operation successful after ${context.attemptNumber} attempt(s)`,
        attemptNumber: context.attemptNumber,
        maxAttempts: context.maxAttempts,
        ...analyticsData,
      })
    );
  }

  /**
   * Send analytics for failed operations
   */
  private static sendFailureAnalytics(
    context: RetryContext,
    analytics?: RetryAnalytics
  ): void {
    if (!analytics?.dispatch) {
      return;
    }

    const { dispatch, ...analyticsData } = analytics;

    dispatch(
      sendAnalyticsError({
        Method: analyticsData.method || 'RetryManager',
        message:
          analyticsData.message ||
          `Operation failed after ${context.attemptNumber} attempt(s)`,
        attemptNumber: context.attemptNumber,
        maxAttempts: context.maxAttempts,
        ...analyticsData,
      })
    );
  }

  /**
   * Execute operation with retry logic
   */
  async execute<T>(
    operation: RetryOperation<T>,
    options: {
      onSuccess?: RetrySuccessHandler<T>;
      onFailure?: RetryFailureHandler;
      onAttempt?: RetryAttemptHandler;
      analytics?: RetryAnalytics;
    } = {}
  ): Promise<T> {
    const { onSuccess, onFailure, onAttempt, analytics } = options;
    let attemptNumber = 1;
    let lastError: unknown;

    while (this.shouldRetry(attemptNumber)) {
      const isRetry = attemptNumber > 1;
      const backoffDelay = isRetry ? this.calculateDelay(attemptNumber) : 0;

      const context: RetryContext = {
        attemptNumber,
        maxAttempts: this.maxAttempts,
        backoffDelay,
        isRetry,
      };

      try {
        // Apply delay for retries
        if (isRetry) {
          await delay(backoffDelay);
        }

        // Send analytics for the attempt
        RetryManager.sendAttemptAnalytics(context, analytics);

        // Call custom attempt handler
        onAttempt?.(context);

        // Execute the operation
        const result = await operation(context);

        // Operation succeeded
        RetryManager.sendSuccessAnalytics(result, context, analytics);
        onSuccess?.(result, context);

        return result;
      } catch (error) {
        lastError = error;
        attemptNumber++;

        // Check if we should continue retrying
        if (!this.shouldRetry(attemptNumber)) {
          break;
        }
      }
    }

    // All attempts failed
    const finalContext: RetryContext = {
      attemptNumber: attemptNumber - 1,
      maxAttempts: this.maxAttempts,
      backoffDelay: 0,
      isRetry: true,
    };

    RetryManager.sendFailureAnalytics(finalContext, analytics);
    onFailure?.(finalContext);

    throw lastError;
  }

  /**
   * Create a new RetryManager with different options
   */
  static create(options: RetryOptions = {}): RetryManager {
    return new RetryManager(options);
  }

  /**
   * Create a RetryManager with jitter enabled for resilient operations
   */
  static withJitter(baseDelay = 500, maxAttempts = 3): RetryManager {
    return new RetryManager({
      baseDelay,
      maxAttempts,
      enableJitter: true,
      maxDelay: 10000,
    });
  }

  /**
   * Create a RetryManager without jitter for predictable operations
   */
  static withoutJitter(baseDelay = 1000, maxAttempts = 3): RetryManager {
    return new RetryManager({
      baseDelay,
      maxAttempts,
      enableJitter: false,
    });
  }
}
